"use client";

import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Search } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import {
	listingFiltersGlassPillClassName,
	listingToolbarLucideIconClassName,
} from "@/components/sheets/FilterSheet/filter-sheet-glass";
import { cn, sanitizeSearchPath, toSafeQueryString } from "@/lib/utils";
import { ga4Search } from "@/lib/analytics/ga4-ecommerce";

type Props = {
	onExpandedChange?: (expanded: boolean) => void;
};

export default function ListingToolbarSearchPill({ onExpandedChange }: Props) {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const inputId = useId();
	const inputRef = useRef<HTMLInputElement>(null);
	const rootRef = useRef<HTMLFormElement>(null);
	const ignoreOutsideClickRef = useRef(false);

	const urlSearch = searchParams.get("search") ?? "";
	const [expanded, setExpanded] = useState(() => urlSearch.trim().length > 0);
	const [draft, setDraft] = useState(urlSearch);

	useEffect(() => {
		onExpandedChange?.(expanded);
	}, [expanded, onExpandedChange]);

	useEffect(() => {
		setDraft(urlSearch);
		if (urlSearch.trim().length > 0) setExpanded(true);
	}, [urlSearch]);

	useEffect(() => {
		if (!expanded) return;
		const t = window.setTimeout(() => inputRef.current?.focus(), 50);
		return () => window.clearTimeout(t);
	}, [expanded]);

	useEffect(() => {
		if (!expanded) return;
		const onPointerDown = (event: MouseEvent) => {
			if (ignoreOutsideClickRef.current) {
				ignoreOutsideClickRef.current = false;
				return;
			}
			const root = rootRef.current;
			if (!root || root.contains(event.target as Node)) return;
			setDraft(urlSearch);
			setExpanded(urlSearch.trim().length > 0);
		};
		const id = window.setTimeout(() => {
			document.addEventListener("mousedown", onPointerDown);
		}, 0);
		return () => {
			window.clearTimeout(id);
			document.removeEventListener("mousedown", onPointerDown);
		};
	}, [expanded, urlSearch]);

	const openSearch = useCallback(() => {
		ignoreOutsideClickRef.current = true;
		setExpanded(true);
	}, []);

	const applySearch = useCallback(() => {
		inputRef.current?.blur();
		const safe = sanitizeSearchPath(draft);
		const params = new URLSearchParams(searchParams);
		params.delete("page");
		if (!safe || safe.toLowerCase() === "default") {
			params.delete("search");
		} else {
			params.set("search", safe);
			ga4Search(safe);
		}
		const q = toSafeQueryString(params);
		router.push(q ? `${pathname}?${q}` : pathname);
		if (!safe) setExpanded(false);
	}, [draft, pathname, router, searchParams]);

	const onSubmit = useCallback(
		(event: FormEvent) => {
			event.preventDefault();
			applySearch();
		},
		[applySearch],
	);

	return (
		<form
			ref={rootRef}
			onSubmit={onSubmit}
			className={cn(
				listingFiltersGlassPillClassName,
				"box-border h-10 max-h-10 min-h-10 shrink-0 overflow-hidden !p-0 transition-[width] duration-300 ease-[cubic-bezier(0.34,1.2,0.64,1)]",
				expanded
					? "flex min-w-0 flex-1 basis-0 items-center justify-start max-sm:max-w-[calc(100%-6rem)] sm:w-[18rem] sm:max-w-[18rem] sm:flex-none sm:shrink-0 sm:basis-auto"
					: "relative w-10 shrink-0",
			)}
		>
			{expanded ? (
				<>
					<label htmlFor={inputId} className="sr-only">
						Search services
					</label>
					<input
						ref={inputRef}
						id={inputId}
						type="text"
						inputMode="search"
						enterKeyHint="search"
						name="search"
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								e.preventDefault();
								setDraft(urlSearch);
								setExpanded(urlSearch.trim().length > 0);
								inputRef.current?.blur();
							}
						}}
						placeholder="Search…"
						autoComplete="off"
						className="listing-toolbar-search-input h-10 min-w-0 flex-1 border-0 bg-transparent py-0 pl-4 pr-1 text-base leading-10 text-foreground caret-[color:var(--color-brand-primary)] outline-none ring-0 placeholder:text-grey-foreground focus-visible:outline-none focus-visible:ring-0 sm:text-sm dark:text-white dark:placeholder:text-white/70"
					/>
					<button
						type="submit"
						aria-label="Search"
						className="grid h-10 w-10 shrink-0 place-items-center border-0 border-l border-solid border-[color:var(--color-border-standard)] bg-secondary p-0 transition-colors hover:bg-secondary/90 dark:border-[color:var(--glass-border)]"
						onPointerDown={() => inputRef.current?.blur()}
					>
						<Search
							className="listing-toolbar-search-submit-icon size-4 shrink-0 text-white"
							strokeWidth={2.25}
							aria-hidden
						/>
					</button>
				</>
			) : (
				<button
					type="button"
					aria-label="Open search"
					className="absolute inset-0 z-10 grid h-full w-full place-items-center border-0 bg-transparent p-0"
					onPointerDown={(event) => {
						if (event.button !== 0) return;
						event.preventDefault();
						openSearch();
					}}
				>
					<Search
						className={listingToolbarLucideIconClassName}
						strokeWidth={2.25}
						aria-hidden
					/>
				</button>
			)}
		</form>
	);
}
